import { BrowserRouter, Navigate, Route, Routes } from "react-router";

// Toolkit imports
import { ApiCacheProvider } from "sj-ui-toolkit/src/api/cache";
import { CookieConsent } from "sj-ui-toolkit/src/atlas/components";
import { LearnAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useSegment } from "sj-ui-toolkit/src/hooks";
import { ErrorPage } from "sj-ui-toolkit/src/atlas/components/ErrorPage/ErrorPage";

// Context
import { LearnContextProvider } from "./context/LearnContext.jsx";

// Shared layout
import Layout from "./components/Layout/Layout.jsx";

// Route components
import Dashboard from "./components/Dashboard/Dashboard.jsx";
import LessonPage from "./components/Lesson/LessonPage.jsx";
import ExercisePage from "./components/Exercise/ExercisePage.jsx";
import Path from "./components/Path/Path.jsx";
import TournamentPage from "./components/Tournament/TournamentPage.jsx";
import TournamentContentPage from "./components/Tournament/TournamentContentPage.jsx";
import Assessments from "./components/Assessments/Assessments.jsx";

// Guest access component
import GuestLessonAccess from "./components/Guest/GuestLessonAccess.jsx";
import Catalog from "./components/Catalog/Catalog.jsx";
import TestConnection from "./components/TestConnection/TestConnection.jsx";
import FeedbackPage from "./components/Feedback/FeedbackPage.jsx";
import LeaderboardPage from "./components/Leaderboard/LeaderboardPage.jsx";
import AssessmentRedirect from "./components/Redirects/AssessmentRedirect.jsx";
import LegacyLessonRedirect from "./components/Redirects/LegacyLessonRedirect.jsx";
import ProfileAchievementRedirect from "./components/Redirects/ProfileAchievementRedirect.jsx";
import ScormLoginRedirect from "./components/Redirects/ScormLoginRedirect.jsx";

// Temporary redirect constants
const Redirects = {
  journey: "journey",
  assignments: "account/assignments",
  tournamentsFinished: "/tournaments/finished",
  profileAchievements: "/account/achievements",
  signIn: "/user/sign_in",
  modulesCatalog: "/modules/catalog",
};

function App() {
  useSegment();

  return (
    <BrowserRouter>
      <CookieConsent />
      <ApiCacheProvider>
        <LearnContextProvider>
          <Routes>
            <Route path={LearnAppRoutes.route} element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path={LearnAppRoutes.routes.catalog.route} element={<Catalog />} />
              <Route path={LearnAppRoutes.routes.assessments.route} element={<Assessments />} />
              <Route path={LearnAppRoutes.routes.lesson.route} element={<LessonPage />} />
              <Route path={LearnAppRoutes.routes.exercise.route} element={<ExercisePage />} />
              <Route path={LearnAppRoutes.routes.path.route} element={<Path />} />
              <Route
                path={LearnAppRoutes.routes.quest.route}
                element={<Path pathType={"Quest"} />}
              />
              <Route
                path={LearnAppRoutes.routes.tournaments.routes.details.route}
                element={<TournamentPage />}
              />
              <Route
                path={LearnAppRoutes.routes.tournaments.routes.content.route}
                element={<TournamentContentPage />}
              />
              <Route path={LearnAppRoutes.routes.feedback.route} element={<FeedbackPage />} />
              <Route path={LearnAppRoutes.routes.leaderboard.route} element={<LeaderboardPage />} />
              <Route
                path={LearnAppRoutes.routes.assessmentRedirect.route}
                element={<AssessmentRedirect />}
              />
              {/* Redirect legacy lesson routes */}
              <Route
                path={LearnAppRoutes.routes.legacyBeltRedirect.route}
                element={<LegacyLessonRedirect />}
              />
              <Route
                path={LearnAppRoutes.routes.legacyLessonRedirect.route}
                element={<LegacyLessonRedirect />}
              />
              {/* Redirects to make things work while we are transitioning to fully Atlas */}
              {/* Cleanup for the following routes are documented in SHRPA-3105 */}
              {/* Redirect "/journey" => "/" to dashboard */}
              <Route
                path={Redirects.journey}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* Redirect "/account/assignments" to dashboard */}
              <Route
                path={Redirects.assignments}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* Redirect "/tournaments/finished" => "/" to dashboard */}
              <Route
                path={Redirects.tournamentsFinished}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* External redirect to User Profile Achievements */}
              <Route
                path={Redirects.profileAchievements}
                element={<ProfileAchievementRedirect />}
              />

              {/* Redirect /user/sign_in to dashboard for logged-in users */}
              <Route
                path={Redirects.signIn}
                element={<Navigate to={LearnAppRoutes.route} replace />}
              />

              {/* Redirect /modules/catalog to /catalog */}
              <Route
                path={Redirects.modulesCatalog}
                element={<Navigate to={LearnAppRoutes.routes.catalog.route} replace />}
              />

              {/** SCORM Route -> Routes to LessonPage **/}
              <Route path={LearnAppRoutes.routes.scorm.route} element={<LessonPage />} />
              <Route
                path="*"
                element={
                  <ErrorPage
                    redirectRoute={LearnAppRoutes.route}
                    buttonText="Go to Dashboard"
                    automaticRedirect={false}
                  />
                }
              />
            </Route>
            {/** Guest Access is outside of the Layout **/}
            <Route path={LearnAppRoutes.routes.guest.route} element={<GuestLessonAccess />} />
            {/** Test Connection is outside of the Layout so it's accessible without auth **/}
            <Route path={LearnAppRoutes.routes.testConnection.route} element={<TestConnection />} />
            {/* SCORM Redirect Route -> Routes to Login */}
            <Route
              path={LearnAppRoutes.routes.scorm_redirect.route}
              element={<ScormLoginRedirect />}
            />
          </Routes>
        </LearnContextProvider>
      </ApiCacheProvider>
    </BrowserRouter>
  );
}

export default App;
